import { ArrowRight, Check, Search } from 'lucide-react';

import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

interface LearnBasicsEvidenceQuestionsProps {
  questions: string[];
  /** Index of the question whose answer is currently shown in the Evidence tab. */
  askedIndex: number | null;
  onAsk: (index: number, question: string) => void;
  /**
   * `row` is the dense list the control arm's accordion opens inline. `card` is
   * the guided-tour treatment: taller cards with a framed icon and no trailing
   * affordance, since the tour screen has the whole rail to itself.
   */
  variant?: 'row' | 'card';
  /**
   * Lock the list — used while the note is still generating, since the answer is
   * grounded in that note and there is nothing to cite yet.
   */
  disabled?: boolean;
}

/**
 * The three suggested evidence questions. Shared by the accordion row (control
 * arm) and the guided-tour evidence screen (treatment arm) so both present the
 * same list and write through the same handler.
 */
export function LearnBasicsEvidenceQuestions({
  questions,
  askedIndex,
  onAsk,
  variant = 'row',
  disabled = false,
}: LearnBasicsEvidenceQuestionsProps) {
  const isCard = variant === 'card';

  return (
    <div
      className={cn(
        'flex flex-col transition-opacity',
        isCard ? 'gap-3' : 'gap-2',
        disabled && 'pointer-events-none opacity-50'
      )}
    >
      {questions.map((question, index) => {
        // The asked question keeps a selected state so the user can see which
        // answer the Evidence tab is showing.
        const isSelected = askedIndex === index;

        if (isCard) {
          return (
            <button
              key={question}
              type="button"
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => onAsk(index, question)}
              className={cn(
                'flex items-center gap-2.5 rounded-xl border p-4 text-left transition-colors',
                isSelected
                  ? 'border-border-strong bg-surface shadow'
                  : 'border-border hover:border-border-hover hover:bg-surface-accent'
              )}
            >
              <span
                className={cn(
                  'flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors',
                  // Both states are entity-muted fills so the frame stays a
                  // visible tile: `surface-accent` sits within 1% of the page
                  // fill behind it and reads as no frame at all.
                  isSelected ? 'bg-entity-yellow-muted' : 'bg-entity-neutral-muted'
                )}
              >
                <Search className="size-4 text-text-primary" />
              </span>
              <TypographyP2 className="flex-1 text-text-primary">{question}</TypographyP2>
            </button>
          );
        }

        return (
          <button
            key={question}
            type="button"
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => onAsk(index, question)}
            className={cn(
              'flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-left transition-colors',
              isSelected
                ? 'border-accent-forest/40 bg-accent-forest/[0.06]'
                : 'border-border bg-surface hover:border-accent-forest/40 hover:bg-accent-forest/[0.04]'
            )}
          >
            <Search className="mt-0.5 size-4 shrink-0 text-accent-forest" />
            <TypographyP2 className="flex-1 text-text-primary">{question}</TypographyP2>
            {isSelected ? (
              <Check className="mt-0.5 size-4 shrink-0 text-accent-forest" />
            ) : (
              <ArrowRight className="mt-0.5 size-4 shrink-0 text-text-tertiary rtl:-scale-x-100" />
            )}
          </button>
        );
      })}
    </div>
  );
}
